import { useState, type CSSProperties, type FormEvent } from 'react'
import type { IslandProps } from '../registry'
import { supabase } from '../../lib/supabase'
import { toast } from '../../lib/toast'
import {
  localDay,
  useTodayData,
  type ColdContact,
  type TodayDeal,
  type TodayTask,
} from './useTodayData'

/**
 * Today's working sections: Tasks (with quick add and one-click done) and
 * Up next (deals closing soon, contacts going cold, or a first-step prompt
 * on an empty account).
 *
 * Replaces the legacy Tasks section that `loadHome()` in crm/index.html
 * rendered as static rows linking to the Tasks tab. The host remounts this
 * island each time it rebuilds Today. The add/edit modals and drawers stay
 * legacy; this island opens them through the host's window functions.
 */

type LegacyFn = (...args: unknown[]) => unknown
function callLegacy(name: string, ...args: unknown[]): boolean {
  const fn = (window as unknown as Record<string, unknown>)[name]
  if (typeof fn !== 'function') return false
  try {
    ;(fn as LegacyFn)(...args)
  } catch (e) {
    console.warn(`[today] ${name} failed`, e)
  }
  return true
}

function goToTab(tab: string): void {
  const el = document.querySelector(`.nav-tab[data-tab="${tab}"]`)
  if (el) callLegacy('switchTab', el)
}

function openNew(tab: string, fn: string): void {
  goToTab(tab)
  setTimeout(() => callLegacy(fn), 80)
}

function fmtDay(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function fmtMoney(v: number | null): string {
  if (!v) return ''
  if (v >= 1000) return '$' + (Math.round(v / 100) / 10).toString().replace(/\.0$/, '') + 'k'
  return '$' + v
}

function daysAgo(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000))
}

const S = {
  dot: (color: string): CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),
  main: { flex: 1, minWidth: 0, textAlign: 'left' } as CSSProperties,
  title: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as CSSProperties,
  sub: {
    fontSize: 12,
    color: 'var(--text-dim)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as CSSProperties,
  meta: (color = 'var(--text-muted)'): CSSProperties => ({
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    color,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  }),
  badge: (bg: string, color: string): CSSProperties => ({
    background: bg,
    color,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  }),
  rowBtn: {
    background: 'none',
    border: 0,
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
    cursor: 'pointer',
  } as CSSProperties,
  check: {
    width: 18,
    height: 18,
    borderRadius: 5,
    border: '1.5px solid var(--border2)',
    background: 'transparent',
    cursor: 'pointer',
    flexShrink: 0,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent)',
    fontSize: 12,
    lineHeight: 1,
  } as CSSProperties,
  group: {
    padding: '14px 20px 6px',
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--text-dim)',
  } as CSSProperties,
  smallBtn: { padding: '4px 10px', fontSize: 11 } as CSSProperties,
}

function QuickAdd({ userId, onAdded }: { userId: string; onAdded: () => void }) {
  const [title, setTitle] = useState('')
  const [due, setDue] = useState('0')
  const [saving, setSaving] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const t = title.trim()
    if (!t) {
      toast('Type what needs doing first', 'error')
      return
    }
    setSaving(true)
    const { error } = await supabase.from('tasks').insert({
      user_id: userId,
      title: t,
      due_date: localDay(Number(due)),
      done: false,
      updated_at: new Date().toISOString(),
    })
    setSaving(false)
    if (error) {
      toast('Could not add the task: ' + error.message, 'error')
      return
    }
    setTitle('')
    toast(due === '0' ? 'Task added for today' : 'Task added', 'success')
    onAdded()
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'flex',
        gap: 8,
        padding: '12px 20px',
        borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}
    >
      <label htmlFor="today-quick-add" style={{ position: 'absolute', left: -9999 }}>
        New task
      </label>
      <input
        id="today-quick-add"
        className="search-box"
        style={{ maxWidth: 'none', minWidth: 160 }}
        placeholder="Add a task, e.g. Call Dana about the pilot"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        disabled={saving}
        autoComplete="off"
      />
      <select
        className="filter-select"
        aria-label="Due"
        value={due}
        onChange={(e) => setDue(e.target.value)}
        disabled={saving}
      >
        <option value="0">Today</option>
        <option value="1">Tomorrow</option>
        <option value="7">Next week</option>
      </select>
      <button type="submit" className="add-btn" style={{ marginLeft: 0 }} disabled={saving}>
        {saving ? 'Adding…' : 'Add task'}
      </button>
    </form>
  )
}

function TaskRow({
  task,
  today,
  onDone,
}: {
  task: TodayTask
  today: string
  onDone: (t: TodayTask) => void
}) {
  const over = !!task.due_date && task.due_date < today
  const nm = task.contacts?.full_name || task.contacts?.first_name || ''
  const co = task.contacts?.company_name || ''
  const open = () => {
    if (task.contact_id) callLegacy('openContactDrawer', task.contact_id)
    else goToTab('tasks')
  }
  return (
    <div className="home-task-row" style={{ cursor: 'default' }}>
      <button
        type="button"
        style={S.check}
        onClick={() => onDone(task)}
        aria-label={`Mark "${task.title}" done`}
        title="Mark done"
      />
      <button type="button" style={S.rowBtn} onClick={open}>
        <div style={S.main}>
          <div style={S.title}>{task.title}</div>
          {nm && (
            <div style={S.sub}>
              {nm}
              {co ? ' · ' + co : ''}
            </div>
          )}
        </div>
        <span style={S.meta(over ? 'var(--red)' : 'var(--text-muted)')}>
          {over ? 'Overdue · ' : ''}
          {fmtDay(task.due_date)}
        </span>
      </button>
    </div>
  )
}

function DealRow({ deal, today }: { deal: TodayDeal; today: string }) {
  const late = !!deal.close_date && deal.close_date < today
  return (
    <div className="home-task-row" style={{ cursor: 'default' }}>
      <button type="button" style={S.rowBtn} onClick={() => callLegacy('openDealDrawer', deal.id)}>
        <div style={S.dot('var(--accent)')} />
        <div style={S.main}>
          <div style={S.title}>{deal.title}</div>
          <div style={S.sub}>
            {deal.stage}
            {deal.accounts?.company_name ? ' · ' + deal.accounts.company_name : ''}
            {deal.value ? ' · ' + fmtMoney(deal.value) : ''}
          </div>
        </div>
        <span style={S.meta(late ? 'var(--red)' : 'var(--text-muted)')}>
          {late ? 'Past close · ' : 'Closes '}
          {fmtDay(deal.close_date)}
        </span>
      </button>
    </div>
  )
}

function ColdRow({ c }: { c: ColdContact }) {
  const p = c.contacts
  const name = p?.full_name || [p?.first_name, p?.last_name].filter(Boolean).join(' ') || '—'
  return (
    <div className="home-task-row" style={{ cursor: 'default' }}>
      <button
        type="button"
        style={S.rowBtn}
        onClick={() => callLegacy('openContactDrawer', c.contact_id)}
      >
        <div style={S.dot('var(--blue)')} />
        <div style={S.main}>
          <div style={S.title}>{name}</div>
          <div style={S.sub}>
            {p?.company_name || ''}
            {p?.company_name && c.lifecycle_stage ? ' · ' : ''}
            {c.lifecycle_stage || ''}
          </div>
        </div>
        <span style={S.meta()}>
          {c.last_contacted ? `Last touch ${daysAgo(c.last_contacted)}d ago` : 'Never contacted'}
        </span>
      </button>
    </div>
  )
}

function FirstSteps() {
  return (
    <div className="empty-state" role="status" style={{ padding: '36px 24px' }}>
      <div className="es-title" style={{ fontSize: 18 }}>
        Add your first contact
      </div>
      <div className="es-msg">
        Hunts works from your people and deals. Add someone you&apos;re talking to, and this list
        fills with who to follow up and which deals close soon.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          type="button"
          className="es-cta"
          onClick={() => openNew('contacts', 'openAddContact')}
        >
          Add a contact
        </button>
        <button
          type="button"
          className="sec-btn"
          onClick={() => openNew('pipeline', 'openAddDeal')}
        >
          Create a deal
        </button>
      </div>
    </div>
  )
}

export default function TodayFocus({ user }: IslandProps) {
  const userId = user?.id ?? null
  const { data, reload, removeTask } = useTodayData(userId)
  if (!userId) return null

  const today = localDay()
  const overdue = data.tasks.filter((t) => t.due_date && t.due_date < today).length
  const dueToday = data.tasks.filter((t) => t.due_date === today).length

  const markDone = async (t: TodayTask) => {
    removeTask(t.id)
    const stamp = new Date().toISOString()
    let { error } = await supabase
      .from('tasks')
      .update({ done: true, status: 'Completed', updated_at: stamp })
      .eq('id', t.id)
    // Older rows may predate the status column (legacy toggleTaskDone does the same).
    if (error?.message?.includes('column')) {
      ;({ error } = await supabase
        .from('tasks')
        .update({ done: true, updated_at: stamp })
        .eq('id', t.id))
    }
    if (error) {
      toast('Could not complete the task: ' + error.message, 'error')
      void reload()
      return
    }
    toast(`Done: ${t.title}`, 'success')
  }

  const emptyAccount = data.status === 'ready' && data.contactCount === 0 && data.dealCount === 0
  const nothingNext = data.deals.length === 0 && data.cold.length === 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="home-section" aria-labelledby="today-tasks-title">
        <div className="home-section-head">
          <h2 className="home-section-title" id="today-tasks-title" style={{ margin: 0 }}>
            Tasks
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {overdue > 0 && (
              <span style={S.badge('rgba(248,113,113,.1)', 'var(--red)')}>{overdue} overdue</span>
            )}
            {dueToday > 0 && (
              <span style={S.badge('var(--amber-glow)', 'var(--amber)')}>{dueToday} today</span>
            )}
            <button
              type="button"
              className="sec-btn"
              style={S.smallBtn}
              onClick={() => goToTab('tasks')}
            >
              All tasks
            </button>
          </div>
        </div>
        <QuickAdd userId={userId} onAdded={() => void reload()} />
        <div className="home-section-body">
          {data.status === 'loading' && <div className="home-empty">Loading…</div>}
          {data.status === 'error' && (
            <div className="home-empty" role="alert">
              Couldn&apos;t load tasks.{' '}
              <button
                type="button"
                className="sec-btn"
                style={S.smallBtn}
                onClick={() => void reload()}
              >
                Try again
              </button>
            </div>
          )}
          {data.status === 'ready' && data.tasks.length === 0 && (
            <div className="home-empty">
              Nothing due today. Add a task above, or log a call on a contact and the follow-up
              lands here.
            </div>
          )}
          {data.status === 'ready' &&
            data.tasks.map((t) => <TaskRow key={t.id} task={t} today={today} onDone={markDone} />)}
        </div>
      </section>

      <section className="home-section" aria-labelledby="today-next-title">
        <div className="home-section-head">
          <h2 className="home-section-title" id="today-next-title" style={{ margin: 0 }}>
            Up next
          </h2>
          <button
            type="button"
            className="sec-btn"
            style={S.smallBtn}
            onClick={() => goToTab('pipeline')}
          >
            Deals
          </button>
        </div>
        <div className="home-section-body">
          {data.status === 'loading' && <div className="home-empty">Loading…</div>}
          {emptyAccount && <FirstSteps />}
          {data.status === 'ready' && !emptyAccount && nothingNext && (
            <div className="home-empty">
              No deals close in the next two weeks, and everyone has heard from you in the last{' '}
              {data.rotDays} days.
            </div>
          )}
          {data.status === 'ready' && !emptyAccount && data.deals.length > 0 && (
            <>
              <div style={S.group}>Deals closing soon</div>
              {data.deals.map((d) => (
                <DealRow key={d.id} deal={d} today={today} />
              ))}
            </>
          )}
          {data.status === 'ready' && !emptyAccount && data.cold.length > 0 && (
            <>
              <div style={S.group}>Going cold · no touch in {data.rotDays}+ days</div>
              {data.cold.map((c) => (
                <ColdRow key={c.contact_id} c={c} />
              ))}
            </>
          )}
        </div>
      </section>
    </div>
  )
}
